import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, ExternalLink, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import gamilIcon from "../../assets/images/gmail.webp";
import microsoftIcon from "../../assets/images/outlook.webp";
import yahooIcon from "../../assets/images/yahoopng.png";
import zohoIcon from "../../assets/images/zohomail.png";

export default function OAuthStepsModal({ open, onClose }) {
  const providers = [
    {
      id: "gmail",
      name: "Gmail",
      icon: gamilIcon,
      steps: [
        {
          title: "Choose Gmail OAuth",
          content:
            "Enter your gmail account email id. You’ll be redirected to Google’s secure sign-in page.",
        },
        {
          title: "Sign in to Google",
          content:
            "Log in using your Gmail account. If 2-step verification is enabled, complete the verification.",
        },
        {
          title: "Grant Required Permissions",
          content:
            "Approve access so we can send emails on your behalf and track delivery. Your password is never shared.",
          link: "https://support.google.com/accounts/answer/3466521",
        },
        {
          title: "Connection Complete",
          content:
            "After approval, you’ll be redirected back and your Gmail account will be ready to send campaigns.",
        },
      ],
    },
    {
      id: "outlook",
      name: "Outlook",
      icon: microsoftIcon,
      steps: [
        {
          title: "Select Outlook OAuth",
          content:
            "Enter your Outlook account email id. You’ll be redirected to Microsoft’s login page.",
        },
        {
          title: "Sign in to Microsoft",
          content:
            "Log in using your Outlook or Microsoft 365 account credentials.",
        },
        {
          title: "Allow Access",
          content:
            "Grant permission to send emails and access basic mailbox info required for delivery tracking.",
          link: "https://learn.microsoft.com/en-us/azure/active-directory/develop/permissions-consent-overview",
        },
        {
          title: "Account Connected",
          content:
            "Once approved, your Outlook email is securely connected and ready to use.",
        },
      ],
    },
    {
      id: "zoho",
      name: "Zoho",
      icon: zohoIcon,
      steps: [
        {
          title: "Choose Zoho OAuth",
          content:
            "Enter your Zoho account email id. You’ll be redirected to Zoho’s authentication page.",
        },
        {
          title: "Log in to Zoho",
          content:
            "Enter your Zoho Mail credentials and complete MFA if enabled.",
        },
        {
          title: "Approve Permissions",
          content:
            "Allow access to send emails on your behalf. This does not expose your password.",
          link: "https://www.zoho.com/mail/help/adminconsole/oauth.html",
        },
        {
          title: "Connected Successfully",
          content:
            "After approval, your Zoho Mail account will be connected and ready to send emails.",
        },
      ],
    },
    {
      id: "yahoo",
      name: "Yahoo ",
      icon: yahooIcon,
      steps: [
        {
          title: "Choose Yahoo OAuth",
          content:
            "Enter your Yahoo email address. You’ll be redirected to Yahoo’s secure sign-in page.",
        },
        {
          title: "Sign in to Yahoo",
          content:
            "Log in with your Yahoo Mail account. If two-step verification is enabled, complete the verification.",
        },
        {
          title: "Grant Required Permissions",
          content:
            "Approve access so we can send emails on your behalf and track delivery. Your password is never shared.",
          link: "https://help.yahoo.com/kb/SLN15241.html",
        },
        {
          title: "Connection Complete",
          content:
            "After approval, you’ll be redirected back and your Yahoo Mail account will be ready to send campaigns.",
        },
      ],
    },
  ];

  const benefits = [
    "No passwords or app passwords required",
    "Industry-standard secure authentication",
    "Access can be revoked anytime from provider settings",
    "Faster and safer than SMTP setup",
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogContent
        className="
          w-[95vw] 
          sm:w-[90vw] 
          md:w-full 
          max-w-[720px] 
          overflow-visible
          p-0
        "
      >
        {/* Single scrollable container for all content */}
        <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg sm:text-xl">
              Connect Email via OAuth
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Securely connect your email account without sharing your password
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col space-y-5">
            {/* Tabs */}
            <Tabs defaultValue="gmail" className="w-full">
              {/* Provider Tabs */}
              <TabsList
                className="
                  flex
                  gap-2 
                  mb-4 
                  p-1
                  scrollbar-hide
                "
              >
                {providers.map((provider) => (
                  <TabsTrigger
                    key={provider.id}
                    value={provider.id}
                    className="
                      flex 
                      items-center 
                      gap-2 
                      px-3 
                      py-2 
                      whitespace-nowrap
                      data-[state=active]:bg-gradient-to-r 
                      data-[state=active]:from-blue-600 
                      data-[state=active]:to-indigo-600 
                      data-[state=active]:text-white
                    "
                  >
                    <img src={provider.icon} className="h-4 w-4" alt={provider.name} />
                    <span className="text-xs sm:text-sm">{provider.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Tab Content */}
              {providers.map((provider) => (
                <TabsContent
                  key={provider.id}
                  value={provider.id}
                  className="
                    space-y-3 
                    max-h-[300px] 
                    sm:max-h-[350px] 
                    overflow-y-auto 
                    pr-2
                  "
                >
                  {provider.steps.map((step, index) => (
                    <div
                      key={index}
                      className="
                        border 
                        rounded-lg 
                        p-3 sm:p-4 
                        bg-gray-50/60
                      "
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div
                            className="
                              flex 
                              items-center 
                              justify-center 
                              w-5 h-5 
                              sm:w-6 sm:h-6 
                              bg-blue-600 
                              text-white 
                              text-xs 
                              rounded-full
                              shrink-0
                            "
                          >
                            {index + 1}
                          </div>

                          <h3 className="font-semibold text-sm sm:text-base">
                            {step.title}
                          </h3>
                        </div>

                        {step.link && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs shrink-0"
                            onClick={() => window.open(step.link, "_blank")}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            <span className="hidden sm:inline">Learn more</span>
                          </Button>
                        )}
                      </div>

                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        {step.content}
                      </p>
                    </div>
                  ))}
                </TabsContent>
              ))}
            </Tabs>

            {/* Benefits */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                Why OAuth is Recommended
              </h4>

              <div className="space-y-2">
                {benefits.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-xs sm:text-sm text-gray-600"
                  >
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div
              className="
                flex 
                flex-row 
                gap-2 
                sm:justify-end 
                pt-4 
                border-t
              "
            >
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full sm:w-auto"
              >
                Close
              </Button>

              <Button onClick={onClose} className="main-button w-full sm:w-auto">
                Continue to OAuth
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}